import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  TrendingUp, 
  DollarSign, 
  AlertTriangle, 
  BarChart3, 
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Zap,
  RefreshCw
} from 'lucide-react';
import { DateRangePicker, rangeQuery } from './common/DateRangePicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfitDashboard = () => {
  const navigate = useNavigate();
  const [productMetrics, setProductMetrics] = useState([]);
  const [categoryMetrics, setCategoryMetrics] = useState([]);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState({
    profit_boost_enabled: false,
    auto_sort_enabled: false,
    smart_suggestions_enabled: true
  });
  const [loading, setLoading] = useState(true);

  // Renderiza un TranslatedField ({es,en,cat,fr}) o un string sin romper React.
  const localize = (v) => {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') return v.es || v.en || v.cat || v.fr || Object.values(v)[0] || '';
    return String(v);
  };

  const [range, setRange] = useState({ from: '', to: '' });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const loadData = async () => {
    const token = localStorage.getItem('admin_token');
    const headers = { 'Authorization': `Bearer ${token}` };
    const qs = rangeQuery(range);

    try {
      const [productsRes, categoriesRes, summaryRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/profit/products?${qs}`, { headers }),
        fetch(`${API_URL}/api/analytics/profit/categories?${qs}`, { headers }),
        fetch(`${API_URL}/api/analytics/profit/summary?${qs}`, { headers }),
        fetch(`${API_URL}/api/analytics/profit/settings`)
      ]);

      if (productsRes.ok) setProductMetrics(await productsRes.json());
      if (categoriesRes.ok) setCategoryMetrics(await categoriesRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (settingsRes.ok) setSettings(await settingsRes.json());
    } catch (error) {
      console.error('Error loading profit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (key, value) => {
    const token = localStorage.getItem('admin_token');
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);

    try {
      await fetch(`${API_URL}/api/analytics/profit/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newSettings)
      });
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const getMarginColor = (category) => {
    switch (category) {
      case 'high': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-amber-100 text-amber-800';
      case 'low': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getMarginLabel = (category) => {
    switch (category) {
      case 'high': return 'Alto';
      case 'medium': return 'Medio';
      case 'low': return 'Bajo';
      default: return '-';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <RefreshCw size={32} className="animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-stone-200 rounded-lg transition-colors"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Inteligencia de Beneficios</h2>
              <p className="text-gray-500">Optimiza recomendaciones basándote en margen bruto</p>
            </div>
          </div>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-stone-800 text-white rounded-lg hover:bg-stone-700"
            data-testid="refresh-profit-btn"
          >
            <RefreshCw size={16} />
            Actualizar
          </button>
        </div>

      {/* Profit Boost Mode Toggle */}
      <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Zap className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Modo Profit Boost</h3>
                <p className="text-sm text-gray-500">
                  Prioriza productos de alto margen en las recomendaciones
                </p>
              </div>
            </div>
            <Switch
              checked={settings.profit_boost_enabled}
              onCheckedChange={(v) => updateSettings('profit_boost_enabled', v)}
              data-testid="profit-boost-switch"
            />
          </div>
        </CardContent>
      </Card>

      <div className="bg-white rounded-xl border shadow-sm p-3 space-y-1" data-testid="profit-range">
        <p className="text-xs text-gray-500">{range.from || range.to ? 'Periodo seleccionado' : 'Últimos 30 días (elige fechas para otro periodo)'}</p>
        <DateRangePicker value={range} onChange={setRange} onClear={() => setRange({ from: '', to: '' })} idPrefix="profit-range" />
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Ingresos Estimados</p>
                  <p className="text-2xl font-bold">{(summary.total_estimated_revenue ?? 0).toFixed(2)}€</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Beneficio Estimado</p>
                  <p className="text-2xl font-bold text-green-600">{(summary.total_estimated_profit ?? 0).toFixed(2)}€</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Ratio Beneficio</p>
                  <p className="text-2xl font-bold">{summary.overall_profit_ratio ?? 0}%</p>
                </div>
                <BarChart3 className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Unidades Vendidas</p>
                  <p className="text-2xl font-bold">{summary.total_items_sold ?? 0}</p>
                </div>
                <ArrowUpRight className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for Products and Categories */}
      <Tabs defaultValue="products" className="space-y-4">
        <TabsList>
          <TabsTrigger value="products">Por Producto</TabsTrigger>
          <TabsTrigger value="categories">Por Categoría</TabsTrigger>
          <TabsTrigger value="opportunities">Oportunidades</TabsTrigger>
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products">
          <Card>
            <CardHeader>
              <CardTitle>Métricas por Producto</CardTitle>
              <CardDescription>Ordenados por beneficio en el periodo</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Producto</th>
                      <th className="text-right py-3 px-2">PVP</th>
                      <th className="text-right py-3 px-2">Coste</th>
                      <th className="text-right py-3 px-2">Margen</th>
                      <th className="text-center py-3 px-2">Categoría</th>
                      <th className="text-right py-3 px-2">Uds vendidas</th>
                      <th className="text-right py-3 px-2">Ingresos</th>
                      <th className="text-right py-3 px-2">Beneficio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productMetrics.slice(0, 20).map((product) => (
                      <tr key={product.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            {product.is_premium && <span className="text-amber-500">⭐</span>}
                            <span className="font-medium">{localize(product.name)}</span>
                          </div>
                        </td>
                        <td className="text-right py-3 px-2">{(product.price ?? 0).toFixed(2)}€</td>
                        <td className="text-right py-3 px-2 text-gray-500">
                          {(product.cost_unit ?? 0) > 0 ? `${product.cost_unit.toFixed(2)}€` : '-'}
                        </td>
                        <td className="text-right py-3 px-2">
                          {product.cost_unit > 0 ? `${product.margin_percentage}%` : '-'}
                        </td>
                        <td className="text-center py-3 px-2">
                          <Badge className={(product.cost_unit ?? 0) > 0 ? getMarginColor(product.margin_category) : 'bg-gray-100 text-gray-500'}>
                            {(product.cost_unit ?? 0) > 0 ? getMarginLabel(product.margin_category) : 'Sin coste'}
                          </Badge>
                        </td>
                        <td className="text-right py-3 px-2" data-testid={`profit-units-${product.id}`}>{product.units_sold_30d ?? 0}</td>
                        <td className="text-right py-3 px-2">{(product.revenue_30d ?? 0).toFixed(2)}€</td>
                        <td className="text-right py-3 px-2 font-semibold text-green-600">
                          {(product.profit_30d ?? 0).toFixed(2)}€
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader>
              <CardTitle>Métricas por Categoría</CardTitle>
              <CardDescription>Rendimiento agregado por categoría</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Categoría</th>
                      <th className="text-left py-3 px-2">Sección</th>
                      <th className="text-right py-3 px-2">Productos</th>
                      <th className="text-right py-3 px-2">Ventas</th>
                      <th className="text-right py-3 px-2">Ingresos</th>
                      <th className="text-right py-3 px-2">Beneficio</th>
                      <th className="text-right py-3 px-2">Margen Medio</th>
                      <th className="text-right py-3 px-2">% del beneficio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryMetrics.map((cat) => (
                      <tr key={cat.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2 font-medium">{localize(cat.name)}</td>
                        <td className="py-3 px-2">
                          <Badge variant="outline">{cat.parent_type}</Badge>
                        </td>
                        <td className="text-right py-3 px-2">{cat.product_count ?? 0}</td>
                        <td className="text-right py-3 px-2" data-testid={`profit-cat-units-${cat.id}`}>{cat.units_sold_30d ?? 0}</td>
                        <td className="text-right py-3 px-2">{(cat.revenue_30d ?? 0).toFixed(2)}€</td>
                        <td className="text-right py-3 px-2 font-semibold text-green-600">
                          {(cat.profit_30d ?? 0).toFixed(2)}€
                        </td>
                        <td className="text-right py-3 px-2">{cat.margin_percentage ?? 0}%</td>
                        <td className="text-right py-3 px-2">
                          <span className={`font-semibold ${cat.profit_share >= 30 ? 'text-green-600' : cat.profit_share >= 10 ? 'text-amber-600' : 'text-gray-500'}`}>
                            {cat.profit_share ?? 0}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Opportunities Tab */}
        <TabsContent value="opportunities">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Top Profitable */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowUpRight className="h-5 w-5 text-green-500" />
                  Top Productos Rentables
                </CardTitle>
                <CardDescription>Productos con alto margen y ventas</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {summary?.top_profitable_products?.map((product, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <div>
                        <p className="font-medium">{localize(product.name)}</p>
                        <p className="text-sm text-gray-500">{product.adds} ventas · {product.margin_pct}% margen</p>
                      </div>
                      <span className="font-bold text-green-600">{(product.profit ?? 0).toFixed(2)}€</span>
                    </div>
                  ))}
                  {(!summary?.top_profitable_products || summary.top_profitable_products.length === 0) && (
                    <p className="text-gray-500 text-center py-4">No hay datos suficientes</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Optimization Opportunities */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Oportunidades de Mejora
                </CardTitle>
                <CardDescription>Productos con alto tráfico pero bajo margen</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {summary?.optimization_opportunities?.map((product, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                      <div>
                        <p className="font-medium">{localize(product.name)}</p>
                        <p className="text-sm text-gray-500">
                          {product.views} ventas · {product.margin_pct}% margen
                        </p>
                      </div>
                      <Badge className="bg-amber-100 text-amber-800">
                        Revisar precio
                      </Badge>
                    </div>
                  ))}
                  {(!summary?.optimization_opportunities || summary.optimization_opportunities.length === 0) && (
                    <p className="text-gray-500 text-center py-4">Sin oportunidades detectadas</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Additional Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Configuración de Optimización</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-ordenar por rendimiento</Label>
                <p className="text-sm text-gray-500">Reordena productos automáticamente según métricas</p>
              </div>
              <Switch
                checked={settings.auto_sort_enabled}
                onCheckedChange={(v) => updateSettings('auto_sort_enabled', v)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Sugerencias inteligentes</Label>
                <p className="text-sm text-gray-500">Usa datos de co-compra para sugerencias</p>
              </div>
              <Switch
                checked={settings.smart_suggestions_enabled}
                onCheckedChange={(v) => updateSettings('smart_suggestions_enabled', v)}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default ProfitDashboard;
